// Aktivasyon kodları ve kullanım durumları
interface ActivationCode {
  code: string;
  isUsed: boolean;
  createdAt: Date;
  usedAt?: Date;
  usedBy?: string;
}

// Başlangıç aktivasyon kodları - gerçek uygulamada bu veriler backend'den gelecek
const ACTIVATION_CODES: ActivationCode[] = [
  { code: 'LGS2024-001', isUsed: false, createdAt: new Date() },
  { code: 'LGS2024-002', isUsed: false, createdAt: new Date() },
  { code: 'LGS2024-003', isUsed: false, createdAt: new Date() },
  { code: 'STUDY2024-001', isUsed: false, createdAt: new Date() },
  { code: 'STUDY2024-002', isUsed: false, createdAt: new Date() },
  { code: 'EDUCATION2024-001', isUsed: false, createdAt: new Date() },
  { code: 'LEARN2024-001', isUsed: false, createdAt: new Date() },
  { code: 'SUCCESS2024-001', isUsed: false, createdAt: new Date() },
  { code: 'DEMO2024-001', isUsed: false, createdAt: new Date() },
  { code: 'DEMO2024-002', isUsed: false, createdAt: new Date() }
];

/**
 * Rastgele aktivasyon kodu üretir
 */
const generateActivationCode = (): string => {
  const prefixes = ['LGS', 'STUDY', 'LEARN', 'SUCCESS', 'EDUCATION'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const year = new Date().getFullYear();
  const randomNum = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  return `${prefix}${year}-${randomNum}`;
};

export const activationService = {
  /**
   * Aktivasyon kodunu doğrular ve kullanıldı olarak işaretler
   * @param code - Doğrulanacak aktivasyon kodu
   * @param userEmail - Kodu kullanan kullanıcının e-postası
   * @returns Promise<{isValid: boolean, message: string}> - Doğrulama sonucu
   */
  validateActivationCode: async (code: string, userEmail?: string): Promise<{isValid: boolean, message: string}> => {
    // Simüle edilmiş API çağrısı
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const activationCode = ACTIVATION_CODES.find(ac => ac.code.toUpperCase() === code.toUpperCase());
    
    if (!activationCode) {
      return { isValid: false, message: 'Geçersiz aktivasyon kodu' };
    }
    
    if (activationCode.isUsed) {
      return { isValid: false, message: 'Bu aktivasyon kodu daha önce kullanılmış' };
    }
    
    // Kodu kullanıldı olarak işaretle
    activationCode.isUsed = true;
    activationCode.usedAt = new Date();
    activationCode.usedBy = userEmail;
    
    return { isValid: true, message: 'Aktivasyon kodu geçerli' };
  },

  /**
   * Mevcut aktivasyon kodlarını getirir (admin paneli için)
   * @returns Promise<ActivationCode[]> - Aktivasyon kodları listesi
   */
  getAllCodes: async (): Promise<ActivationCode[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [...ACTIVATION_CODES];
  },

  /**
   * Kullanılmamış kodları getirir
   * @returns Promise<ActivationCode[]> - Kullanılmamış kodlar
   */
  getUnusedCodes: async (): Promise<ActivationCode[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return ACTIVATION_CODES.filter(code => !code.isUsed);
  },

  /**
   * Kullanılmış kodları getirir
   * @returns Promise<ActivationCode[]> - Kullanılmış kodlar
   */
  getUsedCodes: async (): Promise<ActivationCode[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return ACTIVATION_CODES.filter(code => code.isUsed);
  },

  /**
   * Yeni aktivasyon kodları üretir (admin paneli için)
   * @param count - Üretilecek kod sayısı
   * @returns Promise<string[]> - Üretilen kodlar
   */
  generateActivationCodes: async (count: number = 1): Promise<string[]> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newCodes: string[] = [];
    
    for (let i = 0; i < count; i++) {
      let newCode: string;
      do {
        newCode = generateActivationCode();
      } while (ACTIVATION_CODES.some(ac => ac.code === newCode));
      
      ACTIVATION_CODES.push({
        code: newCode,
        isUsed: false,
        createdAt: new Date()
      });
      
      newCodes.push(newCode);
    }
    
    return newCodes;
  },

  /**
   * Belirli bir aktivasyon kodunu manuel olarak ekler (admin paneli için)
   * @param code - Eklenecek kod
   * @returns Promise<{success: boolean, message: string}> - İşlem sonucu
   */
  addActivationCode: async (code: string): Promise<{success: boolean, message: string}> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (ACTIVATION_CODES.some(ac => ac.code.toUpperCase() === code.toUpperCase())) {
      return { success: false, message: 'Bu kod zaten mevcut' };
    }
    
    ACTIVATION_CODES.push({
      code: code.toUpperCase(),
      isUsed: false,
      createdAt: new Date()
    });
    
    return { success: true, message: 'Aktivasyon kodu başarıyla eklendi' };
  },

  /**
   * Aktivasyon kodunu kaldırır (admin paneli için)
   * @param code - Kaldırılacak kod
   * @returns Promise<{success: boolean, message: string}> - İşlem sonucu
   */
  removeActivationCode: async (code: string): Promise<{success: boolean, message: string}> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = ACTIVATION_CODES.findIndex(ac => ac.code.toUpperCase() === code.toUpperCase());
    if (index > -1) {
      const removedCode = ACTIVATION_CODES[index];
      if (removedCode.isUsed) {
        return { success: false, message: 'Kullanılmış kodlar silinemez' };
      }
      
      ACTIVATION_CODES.splice(index, 1);
      return { success: true, message: 'Aktivasyon kodu başarıyla silindi' };
    }
    
    return { success: false, message: 'Kod bulunamadı' };
  },

  /**
   * Aktivasyon kodu istatistiklerini getirir
   * @returns Promise<{total: number, used: number, unused: number}> - İstatistikler
   */
  getStatistics: async (): Promise<{total: number, used: number, unused: number}> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const total = ACTIVATION_CODES.length;
    const used = ACTIVATION_CODES.filter(code => code.isUsed).length;
    const unused = total - used;
    
    return { total, used, unused };
  }
};